import { LineChart, Line, XAxis, YAxis, CartesianGrid, Legend, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { COLORS } from "./PlotGraph";
import { ClockDriftData } from "../services/rawAudioAnalysisService";
import { RawDataAnalyticsGraphType } from "../analytics/RawDataAnalyticsPage";
import { useMemo } from "react";

enum YAxisUnits {
    SECONDS = "seconds",
    MILLISECONDS = "milliseconds"
}

function dictToGraphableData(
    graphData: { [key: string]: ClockDriftData },
    graphType: RawDataAnalyticsGraphType,
    yAxisUnit: YAxisUnits
): { index: number;[key: string]: number }[] {
    const indexMap: { [minute: number]: { index: number;[key: string]: number } } = {};

    for (const [sessionKey, sessionData] of Object.entries(graphData)) {
        for (let i = 0; i < sessionData.minutes.length-1; i++) {
            const minute = sessionData.minutes[i];
            let y = graphType === RawDataAnalyticsGraphType.ACCUMULATED
                ? sessionData.accumulatedDiffs[i]
                : sessionData.diffInTuts[i];

            // Convert units
            if (yAxisUnit === YAxisUnits.MILLISECONDS) y /= 1000;
            if (yAxisUnit === YAxisUnits.SECONDS) y /= 1_000_000;

            if (!(minute in indexMap)) {
                indexMap[minute] = { index: minute };
            }
            indexMap[minute][sessionKey] = y;
        }
    }

    return Object.values(indexMap).sort((a, b) => a.index - b.index);
}

const EstimatedClockDriftChart = ({ 
    graphData, 
    graphType, 
    indexedSessions
 }: { 
    graphData: { [key: string]: ClockDriftData }, 
    graphType: RawDataAnalyticsGraphType, 
    indexedSessions: { session: string, index: number }[] }
) => {
    let graphLabel = { value: "Clock Drift in seconds", angle: -90, position: "insideBottomLeft" };
    const graphableData = useMemo(() => {
        return dictToGraphableData(graphData, graphType, YAxisUnits.SECONDS);
    }, [graphData, graphType]);
    return (
        <ResponsiveContainer width="100%" height={400} style={{ paddingBottom: "5rem" }}>
            <LineChart
                data={graphableData}
                margin={{
                    top: 0,
                    right: 100,
                    left: 100,
                    bottom: 0,
                }}>
                <CartesianGrid strokeDasharray="5 5 " opacity={0.2} />
                <XAxis dataKey="index" type="number" label={{ value: "Time in minutes", position: "insideBottomLeft" }} />
                <YAxis
                    label={graphLabel}
                />
                <Legend />
                <ReferenceLine y={0} stroke="#000" strokeDasharray="3 3" />
                {indexedSessions.map((session, i) => (
                    <Line
                        key={session.session}
                        type="monotone"
                        dataKey={`${session.session}`}
                        name={`${session.session}`}
                        stroke={`${COLORS[session.index % COLORS.length]}`}
                        activeDot={{ r: 8 }}
                        strokeWidth={1}
                    />
                ))}
                <Tooltip />
            </LineChart>
        </ResponsiveContainer>
    );
};
export default EstimatedClockDriftChart;